import type { ReactNode } from 'react'

import { twJoin } from '@/lib/utils'

interface FormSectionProps {
    // Per-section glyph encodes the section's role (customer, delivery,
    // products…) rather than decorating — one shared marker shape, distinct icon.
    'data-order-group'?: string
    icon: ReactNode
    title: string
    hint?: string
    // Slot on the header's trailing edge, e.g. an "add row" button.
    action?: ReactNode
    children: ReactNode
    className?: string
}

// FormSection is the shared structural unit of the order screens: a titled card
// with a tea-green eyebrow header. Reused across create / edit / detail so the
// three screens read as one system.
export function FormSection({
    icon,
    title,
    hint,
    action,
    children,
    className,
    'data-order-group': orderGroup,
}: FormSectionProps) {
    return (
        <section
            data-order-group={orderGroup}
            className={twJoin('rounded-xl border bg-card shadow-sm', className)}
        >
            {/* `flex-wrap` + `basis` trên khối tiêu đề: khi slot action rộng hơn
                phần còn lại của header (vd 3 nút thanh toán trong cột hẹp), action
                rơi xuống dòng dưới thay vì bóp tiêu đề tới mức chữ bị cắt. */}
            <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-3.5 sm:px-5">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary [&_svg]:size-4">
                    {icon}
                </span>
                <div className="min-w-0 flex-1 basis-40">
                    <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">
                        {title}
                    </h2>
                    {hint && (
                        <p className="mt-0.5 text-xs text-muted-foreground">
                            {hint}
                        </p>
                    )}
                </div>
                {action && <div className="shrink-0">{action}</div>}
            </header>
            <div className="space-y-4 p-4 sm:p-6">{children}</div>
        </section>
    )
}
